import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, Link, useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import LegacyRedirect from './LegacyRedirect'; // Pastikan file ini ada di folder yang sama
import './App.css';

const CATEGORY_LIST = [
  "Tote Bag", "Botol", "Mug", "String Bag", "Tote Bag Kanvas", "Mousepad", "Tote Bag Eco", "Tote PE"
];

const MIDTRANS_URL = "https://app.midtrans.com/payment-links/d5a7906e-1fd8-4a38-8675-6a52163abae0";
const WA_NUMBER = "6281927590190"; 

// HELPER: Reset Scroll & Update Canonical saat ganti halaman
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // 1. Reset Scroll ke atas
    window.scrollTo(0, 0);

    // 2. Logika Canonical Dinamis
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    
    // Set URL absolut (Ganti domain jika perlu)
    // pathname + search memastikan parameter seperti ?page= atau ?q= ikut terekam jika diinginkan
    link.setAttribute('href', `https://soy.biz.id${pathname}${search}`);
    
  }, [pathname, search]);

  return null;
};

// FUNGSI FILTER: Cerdas & SEO Friendly
const getFilteredResults = (indexData, query, isStrictCategory = false) => {
  if (!query) return [];
  const lowerQuery = query.toLowerCase();
  if (isStrictCategory) {
    const regex = new RegExp(`\\b${lowerQuery}\\b`, 'i');
    return indexData.filter(item => item.c && regex.test(item.c));
  } else {
    return indexData.filter(item => 
      (item.t && item.t.toLowerCase().includes(lowerQuery)) || 
      (item.s && item.s.toLowerCase().includes(lowerQuery))
    );
  }
};

// UI: PROMO BANNER
const PromoBanner = () => (
  <div className="promo-banner">
    🚚 <strong>GRATIS ONGKIR SE-PULAU JAWA</strong> UNTUK BELANJA DI ATAS <strong>RP 50.000</strong>!
  </div>
);

// ==========================================
// KOMPONEN HALAMAN STATIS
// ==========================================
const StaticPage = ({ title, content }) => {
  const navigate = useNavigate(); // Dibutuhkan agar header di halaman statis bisa navigasi
  useEffect(() => { document.title = `${title} - SOY BIZ`; }, [title]);
  return (
    <div className="App">
      <header className="App-header">
        <div className="header-top">
          <h1 onClick={() => navigate('/')}>SOY BIZ</h1>
          <div className="header-info-right">
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </header>
      <div className="static-page-container">
        <div className="static-content">
          <h1>{title}</h1>
          <div className="content-body">{content}</div>
          <Link to="/" className="btn-black" style={{display:'inline-block', width:'auto', marginTop:'30px', padding:'10px 25px'}}>← Kembali ke Toko</Link>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 1. KOMPONEN HALAMAN DEPAN
// ==========================================
function StoreFront({ searchIndex }) {
  const [searchParams, setSearchParams] = useSearchParams(); 
  const { categoryName } = useParams(); 
  const navigate = useNavigate();

  const [allProductsInFile, setAllProductsInFile] = useState([]); 
  const [suggestions, setSuggestions] = useState([]); 
  const [selectedProduct, setSelectedProduct] = useState(null); 
  
  const [jsonPage, setJsonPage] = useState(1); 
  const [loading, setLoading] = useState(false);
  const [isFetchingNext, setIsFetchingNext] = useState(false); 
  const [visibleCount, setVisibleCount] = useState(30); 

  const [searchInput, setSearchInput] = useState("");
  const totalJsonFiles = 128;

  const activeQuery = categoryName || searchParams.get('q') || "";
  const pageParam = searchParams.get('page'); 
  const isCategoryMode = !!categoryName;

  useEffect(() => {
    document.title = activeQuery 
      ? `Jual ${activeQuery} Murah - SOY BIZ` 
      : "SOY BIZ ID - Koleksi Souvenir & Tote Bag Unik | Gratis Ongkir Jawa";
  }, [activeQuery]);

  useEffect(() => {
    if (pageParam) {
      const p = parseInt(pageParam);
      if (!isNaN(p) && p >= 1 && p <= totalJsonFiles) setJsonPage(p);
    }
  }, [pageParam, totalJsonFiles]);

  useEffect(() => {
    if (isCategoryMode) setSearchInput(""); 
    else if (searchParams.get('q')) setSearchInput(searchParams.get('q'));
  }, [isCategoryMode, searchParams]);

  useEffect(() => {
    if (pageParam) return; 
    if (activeQuery && searchIndex.length > 0) {
      const matches = getFilteredResults(searchIndex, activeQuery, isCategoryMode);
      if (matches.length > 0) {
        const fileCounts = {};
        let maxCount = 0;
        let bestFile = matches[0].f; 
        for (const m of matches) {
          const f = m.f;
          fileCounts[f] = (fileCounts[f] || 0) + 1;
          if (fileCounts[f] > maxCount) { maxCount = fileCounts[f]; bestFile = f; }
        }
        setJsonPage(prev => (prev !== bestFile ? bestFile : prev));
      }
    }
  }, [activeQuery, searchIndex, isCategoryMode, pageParam]);

  const fetchJsonFile = useCallback(async (fileNumber) => {
    setLoading(true);
    try {
      const response = await fetch(`https://img.soy.biz.id/data/products_full_${fileNumber}.json`);
      if (!response.ok) throw new Error('Error fetch');
      const data = await response.json();
      setAllProductsInFile(data);
      setVisibleCount(30); 
    } catch (err) { console.error(err); } finally { setLoading(false); setIsFetchingNext(false); }
  }, []);

  useEffect(() => { fetchJsonFile(jsonPage); }, [jsonPage, fetchJsonFile]);

  const handlePrevGudang = () => {
    if (jsonPage > 1) {
      const newP = jsonPage - 1;
      setSearchParams(params => { params.set('page', newP); return params; });
      setJsonPage(newP);
    }
  };

  const handleNextGudang = () => {
    if (jsonPage < totalJsonFiles) {
      const newP = jsonPage + 1;
      setSearchParams(params => { params.set('page', newP); return params; });
      setJsonPage(newP);
    }
  };

  const handleJumpToGudang = () => {
    const target = prompt(`Teleport ke Gudang (1 - ${totalJsonFiles})?`);
    if (target !== null) {
        const num = parseInt(target);
        if (!isNaN(num) && num >= 1 && num <= totalJsonFiles) {
          setSearchParams(params => { params.set('page', num); return params; });
          setJsonPage(num);
        }
    }
  };

  const filteredProducts = useMemo(() => {
    if (!activeQuery) return allProductsInFile;
    const lowerQuery = activeQuery.toLowerCase();
    if (isCategoryMode) {
      const regex = new RegExp(`\\b${lowerQuery}\\b`, 'i');
      return allProductsInFile.filter(item => regex.test(`${(item.categories || []).join(" ")} ${(item.tags || []).join(" ")}`));
    } else {
      return allProductsInFile.filter(item => 
        (item.title || "").toLowerCase().includes(lowerQuery) || 
        (item.sku || "").toLowerCase().includes(lowerQuery)
      );
    }
  }, [allProductsInFile, activeQuery, isCategoryMode]);

  const currentDisplayProducts = filteredProducts.slice(0, visibleCount);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.offsetHeight) {
        if (visibleCount < filteredProducts.length) setVisibleCount(prev => prev + 20); 
        else if (visibleCount >= filteredProducts.length && !activeQuery && jsonPage < totalJsonFiles && !isFetchingNext && !loading) {
          setIsFetchingNext(true); 
          setTimeout(() => setJsonPage(prev => prev + 1), 1000); 
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleCount, filteredProducts.length, activeQuery, jsonPage, isFetchingNext, loading]);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchInput(term);
    if (term.length > 2 && searchIndex.length > 0) setSuggestions(getFilteredResults(searchIndex, term, false).slice(0, 50));
    else setSuggestions([]);
  };

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') { setSuggestions([]); navigate(`/?q=${encodeURIComponent(searchInput)}`); }
  };

  return (
    <div className="App">
      <PromoBanner />
      <header className="App-header">
        <div className="header-top">
          <h1 onClick={() => navigate('/')}>SOY BIZ</h1>
          
          <div className="gudang-pagination">
            <button onClick={handlePrevGudang} className="page-btn" disabled={jsonPage <= 1}>&laquo;</button>
            <div className="page-display" onClick={handleJumpToGudang}>
              <span>Gudang</span>
              <strong>#{jsonPage}</strong>
            </div>
            <button onClick={handleNextGudang} className="page-btn" disabled={jsonPage >= totalJsonFiles}>&raquo;</button>
          </div>

          <div className="header-info-right">
            <Link to="/about">About |</Link>
            <Link to="/contact"> Contact |</Link>
            <Link to="/terms"> Terms of Service |</Link>
            <Link to="/privacy"> Privacy Policy</Link>
          </div>
        </div>

        <div className="header-bottom">
          <div className="category-nav">
            <Link to="/" className={`cat-pill ${!isCategoryMode && !searchParams.get('q') ? "active" : ""}`}>Semua</Link>
            {CATEGORY_LIST.map((cat, index) => (
              <Link key={index} to={`/category/${encodeURIComponent(cat)}`} className={`cat-pill ${categoryName === cat ? "active" : ""}`}>{cat}</Link>
            ))}
          </div>

          <div className="search-box">
            <input id="main-search" name="q" type="text" placeholder="Cari Koleksi..." value={searchInput} onChange={handleSearchChange} onKeyDown={handleEnterSearch} />
            {suggestions.length > 0 && (
              <div className="suggestions">
                {suggestions.map((item, index) => (
                  <Link key={index} to={`/product/${item.s}`} onClick={() => setSuggestions([])}>
                    <span>{item.t}</span>
                    <small>Gudang #{item.f}</small>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="product-grid">
        {loading && !isFetchingNext ? (
          <div className="loader"><div className="spin"></div></div>
        ) : (
          currentDisplayProducts.map((item, index) => (
            <div key={`${item.id}-${index}`} className="product-card" onClick={() => setSelectedProduct(item)}>
              <div className="img-wrap">
                  <img src={item.image_link?.startsWith('http') ? item.image_link : `https://img.soy.biz.id/${item.image_link}`} alt={`${item.title} - Souvenir Unik SOY BIZ`} loading="lazy" />
              </div>
              <div className="p-info">
                <h3>{item.title}</h3>
                <p>Rp {Number(item.price || 0).toLocaleString('id-ID')}</p>
              </div>
            </div>
          ))
        )}
      </main>

      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-x" onClick={() => setSelectedProduct(null)}>&times;</button>
            <div className="modal-body-mini">
              <div className="m-img-box">
                <img src={selectedProduct.image_link?.startsWith('http') ? selectedProduct.image_link : `https://img.soy.biz.id/${selectedProduct.image_link}`} alt={selectedProduct.title} />
              </div>
              <div className="m-text-box">
                <small className="g-tag">Gudang {jsonPage}</small>
                <h2>{selectedProduct.title}</h2>
                <p className="m-price">Rp {Number(selectedProduct.price || 0).toLocaleString('id-ID')}</p>
                <div className="m-short-desc">
                  {selectedProduct.short_desc || "Tersedia koleksi barang unik dengan kualitas terbaik. Gratis Ongkir khusus Pulau Jawa!"}
                </div>
                <div className="m-actions">
                  <button className="btn-pink" onClick={() => window.open(MIDTRANS_URL, '_blank')}>BAYAR SEKARANG</button>
                  <button 
  className="btn-black" 
  onClick={() => {
    const message = `Halo Admin SOY BIZ,\n\nSaya ingin order produk ini:\n*${selectedProduct.title}*\nSKU: ${selectedProduct.sku}\n\nLink Produk: https://soy.biz.id/product/${selectedProduct.sku}`;
    window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  }}
>
  WHATSAPP
</button>
				  </div>
                <Link to={`/product/${selectedProduct.sku}`} className="link-detail">Detail Lengkap →</Link>
              </div>
            </div>
          </div>
        </div>
      )}
      {isFetchingNext && <div className="fetch-info">Memuat Gudang #{jsonPage + 1}...</div>}
    </div>
  );
}

// ==========================================
// 2. KOMPONEN HALAMAN DETAIL (SEO OPTIMIZED)
// ==========================================
function ProductPage({ searchIndex }) {
  const { sku } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fNum, setFNum] = useState(null);

  useEffect(() => {
    if (searchIndex.length === 0) return;
    const info = searchIndex.find(item => item.s === sku);
    if (info) {
      setFNum(info.f); 
      fetch(`https://img.soy.biz.id/data/products_full_${info.f}.json`).then(res => res.json()).then(data => {
          const found = data.find(p => p.sku === sku);
          setProduct(found);
          setLoading(false);
          
          if(found) {
            document.title = `${found.title} - SOY BIZ ID Toko Souvenir Indonesia`;
            let metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
              metaDesc.setAttribute("content", found.short_desc || `Beli ${found.title} harga terbaik di SOY BIZ.`);
            }
            let ogImg = document.querySelector('meta[property="og:image"]');
            let fullImg = found.image_link?.startsWith('http') ? found.image_link : `https://img.soy.biz.id/${found.image_link}`;
            if (ogImg) ogImg.setAttribute("content", fullImg);
          }
      });
    } else setLoading(false);
  }, [sku, searchIndex]);

  if (loading) return <div className="loader"><div className="spin"></div></div>;
  if (!product) return <div className="error-view">Produk tidak ditemukan. <Link to="/">Kembali ke Toko</Link></div>;

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.title,
    "image": product.image_link?.startsWith('http') ? product.image_link : `https://img.soy.biz.id/${product.image_link}`,
    "description": product.description?.replace(/<[^>]*>?/gm, ''),
    "sku": product.sku,
    "brand": { "@type": "Brand", "name": "SOY BIZ" },
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "IDR",
      "price": product.price,
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "SOY BIZ" }
    }
  };

  return (
    <div className="App">
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <header className="App-header">
        <div className="header-top">
          <h1 onClick={() => navigate('/')}>SOY BIZ</h1>
          <div className="header-info-right">
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </header>
      
      <div className="detail-view">
        <Link to="/" className="back-btn">← KEMBALI</Link>
        <div className="detail-layout">
           <div className="d-img">
              <img src={product.image_link?.startsWith('http') ? product.image_link : `https://img.soy.biz.id/${product.image_link}`} alt={product.title} />
           </div>
           
           <div className="d-content">
             {/* 1. LINK GUDANG & SKU */}
             <div className="d-meta-top">
               <Link to={`/?page=${fNum}`} className="g-tag" title={`Lihat semua produk di Gudang #${fNum}`}>
                 Gudang #{fNum}
               </Link>
               <span className="sku-tag">SKU: <strong>{product.sku}</strong></span>
             </div>

             <h1>{product.title}</h1>
             <p className="d-price">Rp {Number(product.price).toLocaleString('id-ID')}</p>

             {/* SHORT DESCRIPTION */}
             {product.short_desc && (
               <div className="d-short-desc"><p>{product.short_desc}</p></div>
             )}

             <div className="d-btns">
                <button className="btn-pink" onClick={() => window.open(MIDTRANS_URL, '_blank')}>PEMBAYARAN OTOMATIS</button>
                <button className="btn-black" onClick={() => {
                  const message = `Halo Admin SOY BIZ,\n\nOrder: *${product.title}*\nSKU: ${product.sku}\n\nLink: https://soy.biz.id/product/${product.sku}`;
                  window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
                }}>HUBUNGI ADMIN</button>
             </div>

             {/* 2. LINK KATEGORI & TAGS */}
             <div className="d-taxonomies">
               {product.categories && product.categories.length > 0 && (
                 <div className="taxo-group">
                   <strong>Kategori:</strong>
                   <div className="taxo-list">
                     {product.categories.map((cat, i) => (
                       <Link key={i} to={`/category/${encodeURIComponent(cat)}`} className="taxo-pill">{cat}</Link>
                     ))}
                   </div>
                 </div>
               )}
               {product.tags && product.tags.length > 0 && (
                 <div className="taxo-group">
                   <strong>Tags:</strong>
                   <div className="taxo-list">
                     {product.tags.map((tag, i) => (
                       <Link key={i} to={`/?q=${encodeURIComponent(tag)}`} className="tag-pill">#{tag}</Link>
                     ))}
                   </div>
                 </div>
               )}
             </div>

             <div className="d-desc">
               <h3>Deskripsi Lengkap</h3>
               <p>{product.description}</p>
             </div>
           </div> {/* Tutup d-content */}
        </div> {/* Tutup detail-layout */}
      </div> {/* Tutup detail-view */}
    </div> 
  );
} // Tutup ProductPage - SELESAI

// ==========================================
// MAIN APP ROUTER
// ==========================================
function App() {
  const [searchIndex, setSearchIndex] = useState([]); 
  useEffect(() => { 
    fetch('https://img.soy.biz.id/data/search_index.json')
      .then(res => res.json())
      .then(data => setSearchIndex(data)); 
  }, []);
  
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* 1. Link Lama WooCommerce (Redirect ke Link Baru) */}
        <Route path="/produk/:slug" element={<LegacyRedirect />} />
        
        {/* 2. Link Produk Baru (Gunakan ProductPage, bukan ProductDetail) */}
        <Route path="/product/:sku" element={<ProductPage searchIndex={searchIndex} />} />
        
        {/* 3. Homepage & Kategori */}
        <Route path="/" element={<StoreFront searchIndex={searchIndex} />} />
        <Route path="/category/:categoryName" element={<StoreFront searchIndex={searchIndex} />} />
        
        {/* 4. Halaman Statis */}
<Route path="/about" element={<StaticPage title="Tentang Kami" content={
  <>
    <p><strong>SOY BIZ</strong> adalah platform e-commerce spesialis souvenir kreatif dan merchandise premium yang berbasis di <strong>Temanggung, Jawa Tengah</strong>.</p>
    
    <p>Kami hadir untuk memenuhi kebutuhan koleksi unik, mulai dari tote bag berkualitas tinggi hingga berbagai perlengkapan souvenir yang dirancang dengan standar mutu terbaik. Dengan dukungan infrastruktur teknologi modern, kami mengelola ribuan pilihan produk untuk memastikan pelanggan mendapatkan variasi terbaik dengan harga kompetitif.</p>
    
    <p><strong>Kenapa Berbelanja di SOY BIZ?</strong></p>
    <ul>
      <li><strong>Kualitas Terjamin:</strong> Setiap produk melalui proses seleksi ketat.</li>
      <li><strong>Layanan Efisien:</strong> Integrasi pembayaran otomatis dan dukungan Admin via WhatsApp.</li>
      <li><strong>Logistik Cerdas:</strong> Program Gratis Ongkir se-Pulau Jawa untuk belanja di atas Rp 50.000 sebagai bentuk efisiensi biaya bagi pelanggan kami.</li>
    </ul>
    
    <p>Kami berkomitmen untuk terus berinovasi dalam memberikan layanan belanja souvenir yang aman, transparan, dan objektif bagi seluruh masyarakat Indonesia.</p>
  </>
} />} />
        <Route path="/contact" element={<StaticPage title="Hubungi Kami" content={
  <>
    <p>Kami sangat menghargai setiap masukan dan pertanyaan dari Anda. Tim layanan pelanggan <strong>SOY BIZ</strong> siap membantu Anda setiap hari pada jam operasional.</p>
    
    <div style={{marginTop:'20px'}}>
      <p><strong>Layanan Cepat (WhatsApp):</strong><br />
      <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noreferrer">+{WA_NUMBER}</a></p>
      
      <p><strong>Lokasi Pusat & Distribusi:</strong><br />
      Temanggung, Jawa Tengah, Indonesia</p>
    </div>
    
    <p style={{marginTop:'20px'}}>Untuk pertanyaan mengenai status pesanan 127 ribu produk kami atau kerja sama kemitraan, pastikan Anda mencantumkan SKU produk agar tim kami dapat memberikan data secara objektif dan cepat.</p>
  </>
} />} />
        <Route path="/terms" element={<StaticPage title="Syarat & Ketentuan" content={
  <>
    <p>Selamat datang di SOY BIZ. Dengan mengakses situs ini, Anda menyetujui ketentuan berikut:</p>
    <ol>
      <li><strong>Akurasi Produk:</strong> Kami berusaha menampilkan 127.000+ produk seakurat mungkin. Namun, perbedaan tampilan warna pada layar monitor Anda tetap menjadi tanggung jawab pengguna.</li>
      <li><strong>Harga & Pembayaran:</strong> Harga yang tertera adalah sah pada saat transaksi dilakukan melalui sistem Midtrans atau konfirmasi Admin.</li>
      <li><strong>Kebijakan Pengiriman:</strong> Promo Gratis Ongkir Pulau Jawa berlaku dengan syarat minimal belanja Rp 50.000. Pengiriman dilakukan dari Temanggung menggunakan kurir rekanan kami.</li>
      <li><strong>Pembatalan:</strong> Pesanan yang sudah masuk ke tahap pengemasan tidak dapat dibatalkan secara sepihak.</li>
    </ol>
  </>
} />} />
        <Route path="/privacy" element={<StaticPage title="Kebijakan Privasi" content={
  <>
    <p>Privasi Anda adalah prioritas utama di <strong>SOY BIZ</strong>. Kami hanya mengumpulkan informasi yang diperlukan untuk memproses pesanan Anda.</p>
    <ul>
      <li><strong>Data Pribadi:</strong> Nama, alamat, dan nomor telepon hanya digunakan untuk keperluan pengiriman barang dan validasi pembayaran.</li>
      <li><strong>Keamanan:</strong> Kami tidak menyimpan data kartu kredit atau kredensial perbankan Anda. Semua transaksi diproses secara enkripsi melalui gateway pembayaran Midtrans.</li>
      <li><strong>Cookies:</strong> Kami menggunakan cookies untuk mengoptimalkan performa render halaman agar kunjungan Anda berikutnya lebih cepat.</li>
    </ul>
    <p>Data Anda tidak akan pernah kami jual atau bagikan kepada pihak ketiga untuk kepentingan iklan tanpa izin eksplisit dari Anda.</p>
  </>
} />} />
        
        {/* 5. Catch-all (Jika nyasar, lempar ke Home) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;